import { get_token, getCookie, csrf_headers } from "@/router";
import { view_profile as view_profile_helper } from "@/views/Feed/feed_helper";
import { append_active_organization_id } from "@/utils/organization_profile";
import Swal from "sweetalert2";
import manage_incident_filter_helper from "./manage_incident_filter_helper";
import manage_incident_stage_helper from "./manage_incident_stage_helper";

const API_BASE_URL = `${process.env.VUE_APP_BACKEND_API_URL}/report_incident_management`;

export function get_generated_name(user) {
  return [user.first_name, user.last_name].filter(Boolean).join(" ");
}

export function get_authority_initials(name) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function get_authority_department(user) {
  return user.role.role;
}

export function map_authority(admin) {
  const user = admin;
  const name = get_generated_name(user);

  return {
    id: admin.id ?? user.id,
    name,
    initials: get_authority_initials(name),
    department: get_authority_department(admin, user),
    admin,
  };
}

export function has_authority_id(authority) {
  return authority.id !== undefined && authority.id !== null;
}

export function get_incident_types_table() {
  if (typeof $ === "undefined") return null;
  if (!$.fn?.DataTable) return null;

  const table = $("#incident-types-table");
  return table.length ? table : null;
}

export function build_incident_type_form_data(type_form, extra_data = {}) {
  const data = append_active_organization_id({
    name: type_form.name,
    color: type_form.color,
    ...extra_data,
  });
  if (type_form.icon) data.icon = type_form.icon;

  const form_data = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value !== null && value !== undefined) form_data.append(key, value);
  });
  if (type_form.file instanceof File) {
    form_data.append("file", type_form.file);
  }
  return form_data;
}

export function select_icon_option(icon_option) {
  if (icon_option.uploaded) {
    this.type_form.icon = "";
    this.type_form.file = icon_option.file || icon_option.value;
    return;
  }
  this.type_form.icon = icon_option.value;
  this.type_form.file = null;
}

export function is_icon_option_selected(icon_option) {
  if (icon_option.uploaded) {
    return Boolean(this.type_form.file)
      && (this.type_form.file === icon_option.file || this.type_form.file === icon_option.value);
  }
  return !this.type_form.file && this.type_form.icon === icon_option.value;
}

export function add_uploaded_icon_option(value, name, file = null) {
  if (this.available_icons.some((icon) => icon.value === value)) return;
  this.available_icons.push({ name, value, file, uploaded: true });
}

export function handle_icon_upload(event) {
  const [file] = event.target.files || [];
  event.target.value = "";
  if (!file) return;
  if (!["image/png", "image/jpeg", "image/svg+xml"].includes(file.type)) {
    Swal.fire({
      icon: "error",
      title: "Unsupported file",
      text: "Choose a PNG, JPG, or SVG image.",
    });
    return;
  }
  const reader = new FileReader();
  reader.onload = () => {
    const value = String(reader.result || "");
    const name = file.name.replace(/\.[^.]+$/, "") || "Uploaded icon";
    this.add_uploaded_icon_option(value, name, file);
    this.type_form.icon = "";
    this.type_form.file = file;
  };
  reader.readAsDataURL(file);
}

const manage_incident_helper = {
  ...manage_incident_filter_helper,
  ...manage_incident_stage_helper,
  select_icon_option,
  is_icon_option_selected,
  add_uploaded_icon_option,
  handle_icon_upload,
  view_profile(profile) {
    view_profile_helper(this.$router, profile);
  },
  get_recent_activity_base(report) {
    return ({
      id: Reflect.get(report, "id"),
      reference: Reflect.get(report, "ref_number"),
      date: this.format_incident_activity_date(Reflect.get(report, "date_created")),
    });
  },
  build_resolved_activity(report, assignee) {
    const is_unassigned = assignee === "Unassigned";
    return ({
      ...this.get_recent_activity_base(report),
      actor: is_unassigned ? "Case" : assignee,
      action: is_unassigned ? "was resolved:" : "resolved",
      icon: "bi-check2",
      tone: "bg-success-subtle text-success",
    });
  },
  build_assigned_activity(report, assignee) {
    return ({
      ...this.get_recent_activity_base(report),
      actor: assignee,
      action: "is assigned to",
      icon: "bi-person-check",
      tone: "bg-primary-subtle text-primary",
    });
  },
  build_logged_activity(report) {
    const user = Reflect.get(Object(report), "user");
    const actor = [Reflect.get(Object(user), "full_name"), Reflect.get(Object(user), "email")].find(Boolean);
    return ({
      ...this.get_recent_activity_base(report),
      actor: actor || "A user",
      action: "logged",
      icon: "bi-exclamation-circle",
      tone: "bg-warning-subtle text-warning",
    });
  },
  build_recent_incident_activity(report) {
    const assignee = this.get_incident_assignee(report);
    const incident_status = Reflect.get(Object(report), "incident_status");
    const status = this.normalize_incident_status(Reflect.get(Object(incident_status), "name"));
    if (status === "Resolved") return this.build_resolved_activity(report, assignee);
    if (assignee !== "Unassigned") return this.build_assigned_activity(report, assignee);
    return this.build_logged_activity(report);
  },
  get_incident_reports() {
    Reflect.set(this, "loading", true);
    Reflect.set(this, "incident_error", "");
    this.destroy_incidents_datatable();

    $.ajax({
      url: `${API_BASE_URL}/get_incident_reports/`,
      type: "POST",
      contentType: "application/json",
      data: JSON.stringify({
        page: 1,
        page_size: 100,
      }),
      xhrFields: { withCredentials: true },
      headers: this.build_request_headers(get_token(), true),
      success: (response) => {
        const response_data = Reflect.get(Object(response), "data") || {};
        const reports = Array.isArray(response_data)
          ? response_data
          : Reflect.get(response_data, "results") || [];

        Reflect.set(this, "logged_entries", reports);
        Reflect.set(this, "incident_pagination", Array.isArray(response_data) ? {} : response_data);
        if (this.selected_incident?.id) {
          const refreshed_incident = reports.find(
            (report) => String(report.id) === String(this.selected_incident.id),
          );
          if (refreshed_incident) {
            Reflect.set(this, "selected_incident", refreshed_incident);
            Reflect.set(
              this,
              "selected_incident_assignee",
              this.get_incident_assignee(refreshed_incident),
            );
          }
        }
        this.open_notification_incident();
      },
      error: (xhr) => {
        Reflect.set(this, "incident_error", Reflect.get(Object(xhr.responseJSON), "message") || "Unable to load incident reports.");
      },
      complete: () => {
        Reflect.set(this, "loading", false);
        this.refresh_incidents_datatable_when_ready();
      },
    });
  },
  get_organization_admins() {
    $.ajax({
      url: this.get_incident_admins_url(),
      type: "GET",
      xhrFields: { withCredentials: true },
      headers: this.build_request_headers(get_token(), true),
      beforeSend: () => {
        Reflect.set(this, "loading_admins", true);
      },
      success: (response) => {
        this.destroy_incidents_datatable();
        const response_data = Reflect.get(Object(response), "data");
        Reflect.set(this, "admins", Array.isArray(response_data) ? response_data : []);
      },
      error: (xhr) => {
        const fallback = "Unable to load incident admins.";
        Reflect.set(this, "incident_error", Reflect.get(Object(xhr.responseJSON), "message") || fallback);
      },
      complete: () => {
        Reflect.set(this, "loading_admins", false);
        this.refresh_incidents_datatable_when_ready();
      },
    });
  },
  get_incident_admins_url() {
    const endpoint = this.client_features.show_organisation
      ? "get_organization_admins"
      : "get_system_admins_api";
    return `${API_BASE_URL}/${endpoint}/`;
  },
  handle_incident_admin_change(report, event) {
    const select = Reflect.get(event, "target");
    if (Reflect.get(select, "value") === "Unassigned") {
      return this.handle_incident_unassignment(report, select);
    }
    return this.handle_incident_assignment(report, select);
  },
  handle_incident_unassignment(report, select) {
    const assigned_user_id = this.get_incident_assignee_id(report);
    if (!assigned_user_id) return null;

    return this.unassign_incident_admin(
      assigned_user_id,
      Reflect.get(Object(report), "id"),
      this.get_incident_assignee(report),
      select,
    );
  },
  handle_incident_assignment(report, select) {
    const previous_assignee = this.get_incident_assignee(report);
    const selected_admin = this.get_admin_by_name(Reflect.get(select, "value"));
    if (!selected_admin) return this.restore_incident_assignee(select, previous_assignee);

    return this.assign_incident_admin(
      Reflect.get(selected_admin, "id"),
      Reflect.get(Object(report), "id"),
      previous_assignee,
      select,
    );
  },
  restore_incident_assignee(select, previous_assignee) {
    if (select) Reflect.set(select, "value", previous_assignee);
    Reflect.set(this, "selected_incident_assignee", previous_assignee);
    return null;
  },
  apply_incident_status(report, status_name) {
    if (!report) return;

    const normalized_name = String(status_name).toLowerCase().replace(/[\s-]+/g, "_");
    const status = this.incident_statuses.find((item) => Reflect.get(item, "name") === normalized_name);
    Reflect.set(report, "incident_status", {
      ...Reflect.get(Object(report), "incident_status"),
      id: Reflect.get(Object(status), "id"),
      name: Reflect.get(Object(status), "name") || normalized_name,
    });
  },
  get_incident_by_id(incident_id) {
    const reports = Array.isArray(this.logged_entries) ? this.logged_entries : [];
    return reports.find(
      (report) => String(Reflect.get(report, "id")) === String(incident_id),
    ) || null;
  },
  get_selected_incident_by_id(incident_id) {
    const selected_id = Reflect.get(Object(this.selected_incident), "id");
    return String(selected_id) === String(incident_id) ? this.selected_incident : null;
  },
  set_incident_assignment(report, assigned_admin, status_name) {
    if (!report) return;
    Reflect.set(report, "assigned_users", assigned_admin ? [{ user: assigned_admin }] : []);
    this.apply_incident_status(report, status_name);
  },
  apply_incident_assignment(user_id, incident_id) {
    this.destroy_incidents_datatable();
    const assigned_admin = this.admins.find((admin) => String(Reflect.get(admin, "id")) === String(user_id));
    this.set_incident_assignment(this.get_incident_by_id(incident_id), assigned_admin, "assigned");

    const selected_incident = this.get_selected_incident_by_id(incident_id);
    this.set_incident_assignment(selected_incident, assigned_admin, "assigned");
    if (selected_incident) {
      Reflect.set(this, "selected_incident_assignee", this.get_admin_display_name(assigned_admin));
    }
    this.$nextTick(this.init_incidents_datatable);
  },
  apply_incident_unassignment(incident_id) {
    this.destroy_incidents_datatable();
    this.set_incident_assignment(this.get_incident_by_id(incident_id), null, "pending");

    const selected_incident = this.get_selected_incident_by_id(incident_id);
    this.set_incident_assignment(selected_incident, null, "pending");
    if (selected_incident) Reflect.set(this, "selected_incident_assignee", "Unassigned");
    this.$nextTick(this.init_incidents_datatable);
  },
  assign_incident_admin(user_id, incident_id, previous_assignee, select) {
    return this.update_incident_assignment({
      action: "assign",
      user_id,
      incident_id,
      previous_assignee,
      select,
    });
  },
  unassign_incident_admin(user_id, incident_id, previous_assignee, select) {
    return this.update_incident_assignment({
      action: "unassign",
      user_id,
      incident_id,
      previous_assignee,
      select,
    });
  },
  update_incident_assignment({ action, user_id, incident_id, previous_assignee, select }) {
    const is_selected_incident = String(this.selected_incident?.id) === String(incident_id);
    const payload = append_active_organization_id({
      user_id,
      report_id: incident_id,
      note: is_selected_incident ? String(this.incident_progress_note || "").trim() : "",
    });
    return $.ajax({
      url: `${API_BASE_URL}/${action}_incident_admin/`,
      type: "POST",
      xhrFields: { withCredentials: true },
      headers: this.build_request_headers(get_token(), true),
      data: JSON.stringify(payload),
      success: (response) => this.handle_assignment_success(
        action,
        user_id,
        incident_id,
        response.data,
      ),
      error: (xhr) => this.handle_assignment_error(xhr, action, select, previous_assignee),
    });
  },
  handle_assignment_success(action, user_id, incident_id, progress_entry) {
    if (action === "assign") {
      this.apply_incident_assignment(user_id, incident_id);
    } else {
      this.apply_incident_unassignment(incident_id);
    }
    this.append_incident_progress(
      this.get_incident_by_id(incident_id),
      progress_entry,
    );
    this.append_incident_progress(
      this.get_selected_incident_by_id(incident_id),
      progress_entry,
    );
    Reflect.set(this, "incident_progress_note", "");
    return progress_entry;
  },
  append_incident_progress(report, progress_entry) {
    if (!report) return;

    const progress_history = Array.isArray(report.progress_history)
      ? report.progress_history
      : [];
    const already_added = progress_history.some(
      (entry) => String(entry.id) === String(progress_entry.id),
    );
    if (!already_added) {
      Reflect.set(report, "progress_history", [progress_entry, ...progress_history]);
    }
  },
  handle_assignment_error(xhr, action, select, previous_assignee) {
    this.restore_incident_assignee(select, previous_assignee);
    const fallback = `Unable to ${action} the organization admin.`;
    console.error(Reflect.get(Object(xhr.responseJSON), "message") || fallback);
  },
  get_incident_statuses() {
    return $.ajax({
      url: `${API_BASE_URL}/get_incident_report_status/`,
      type: "GET",
      xhrFields: { withCredentials: true },
      headers: this.build_request_headers(get_token(), true),
      success: (response) => {
        Reflect.set(this, "incident_statuses", Reflect.get(Object(response), "data") || []);
      },
      error: (xhr) => {
        Reflect.set(this, "incident_error", Reflect.get(Object(xhr.responseJSON), "message") || "Unable to load incident statuses.");
      },
      complete: () => this.get_incident_reports(),
    });
  },
  update_incident_status(status_name) {
    if (this.updating_incident_status) return null;

    const status = this.incident_statuses.find((item) => item.name === status_name);
    const report_id = this.selected_incident?.id;
    if (!status || !report_id) return null;

    this.updating_incident_status = true;
    this.incident_error = "";
    const payload = {
      report_id,
      new_status_id: status.id,
      note: String(this.incident_progress_note || "").trim(),
    };
    return $.ajax({
      url: `${API_BASE_URL}/update_incident_status/`,
      type: "POST",
      data: JSON.stringify(append_active_organization_id(payload)),
      xhrFields: { withCredentials: true },
      headers: this.build_request_headers(get_token(), true),
      success: (response) => {
        this.apply_incident_status_change(report_id, status_name);
        const progress_entry = response.data;
        this.append_incident_progress(
          this.get_incident_by_id(report_id),
          progress_entry,
        );
        this.append_incident_progress(
          this.get_selected_incident_by_id(report_id),
          progress_entry,
        );
        this.incident_progress_note = "";
      },
      error: (xhr) => {
        this.incident_error = xhr.responseJSON?.message || "Unable to update the incident status.";
      },
      complete: () => (this.updating_incident_status = false),
    });
  },
  apply_incident_status_change(report_id, status_name) {
    this.destroy_incidents_datatable();
    this.apply_incident_status(this.get_incident_by_id(report_id), status_name);
    this.apply_incident_status(this.get_selected_incident_by_id(report_id), status_name);
    this.$nextTick(this.init_incidents_datatable);
  },
  build_request_headers(token, include_content_type = false) {
    const headers = Object.assign(
      { Authorization: `Token ${token}` },
      csrf_headers(getCookie("csrftoken")),
    );
    if (include_content_type) Reflect.set(headers, "Content-Type", "application/json");
    return headers;
  },
  open_incident_details(report) {
    Reflect.set(this, "selected_incident", report);
    Reflect.set(this, "selected_incident_assignee", this.get_incident_assignee(report));
    Reflect.set(this, "incident_progress_note", "");
    document.body.classList.add("modal-open");
  },
  open_notification_incident() {
    if (this.notification_incident_opened || this.notification_incident_loading) return;

    const route_query = Object(this.$route?.query);
    const incident_id = Reflect.get(route_query, "incident_report_id") ||
      Reflect.get(route_query, "incident_id");
    if (!incident_id) return;

    const incident = this.get_incident_by_id(incident_id);
    if (incident) {
      this.open_routed_incident(incident);
      return;
    }

    this.fetch_incident_report_by_id(incident_id);
  },
  fetch_incident_report_by_id(incident_id) {
    Reflect.set(this, "notification_incident_loading", true);

    $.ajax({
      url: `${API_BASE_URL}/get_incident_report_by_id/`,
      type: "POST",
      data: JSON.stringify({ incident_id }),
      xhrFields: { withCredentials: true },
      headers: this.build_request_headers(get_token(), true),
      success: (response) => {
        const incident = Reflect.get(Object(response), "data");
        if (!incident) return;

        this.open_routed_incident(incident);
      },
      error: (xhr) => {
        const message = Reflect.get(Object(xhr.responseJSON), "message") ||
          "Unable to load the selected incident report.";
        Reflect.set(this, "incident_error", message);
      },
      complete: () => {
        Reflect.set(this, "notification_incident_loading", false);
      },
    });
  },
  open_routed_incident(incident) {
    Reflect.set(this, "notification_incident_opened", true);
    this.open_incident_details(incident);

    const query = { ...this.$route.query };
    delete query.incident_report_id;
    delete query.incident_id;
    this.$router.replace({ query });
  },
  close_incident_details() {
    Reflect.set(this, "selected_incident", null);
    Reflect.set(this, "incident_progress_note", "");
    document.body.classList.remove("modal-open");
  },
};

export default manage_incident_helper;
